<template>
  <div class="card">
    <h3 class="title">{{ name }}</h3>
    <div class="meta">
      <div class="data">
        <div class="icon"><i class="fa-solid fa-map"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.distancia") }}</div>
          <div class="data-value">{{ formatNumberWithComma(distanceKm, 1) }} km</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-bolt"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.potencia") }}</div>
          <div class="data-value">{{formatNumberWithComma(powerKw, 2) }} kW</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-euro-sign"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.precio_kwh") }}</div>
          <div class="data-value">{{ formatNumberWithComma(priceKwh, 2) }} €</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-clock"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.tiempo_carga") }}</div>
          <div class="data-value">{{ chargeTimeMin }} min</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-receipt"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.coste_total") }}</div>
          <div class="data-value">{{ formatNumberWithComma(totalCost, 2) }} €</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-circle" :class="statusClass"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.estado") }}</div>
          <div class="data-value" :class="statusClass">
            {{ estadoTexto }}
          </div>
        </div>
      </div>
      <div class="action action-full-width">
        <button class="btn" @click="navigateToCharger">{{ $t("components.chargerlistitem.ir_al_cargador") }}</button>
      </div>
    </div>

    <!-- Navegación para cargadores agrupados -->
    <div v-if="mostrarNavegacionAgrupada" class="group-navigation">
      <div class="navigation-controls">
        <button class="nav-button" @click="$emit('navigate-previous')" :disabled="mostrarBotonesNavegacionAgrupada">
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <span class="counter">{{ (currentIndex ?? 0) + 1 }} {{ $t("components.chargerlistitem.de") }} {{ groupedCount
        }}</span>
        <button class="nav-button" @click="$emit('navigate-next')" :disabled="mostrarBotonesNavegacionAgrupada">
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Función helper para formatear números con coma como separador decimal
function formatNumberWithComma(value: number, decimals: number): string {
  return value.toFixed(decimals).replace('.', ',');
}

onMounted(() => {
  // Idioma detectado por el navegador
});

const props = defineProps<{
  name: string;
  distanceKm: number;
  priceKwh: number;
  totalCost: number;
  powerKw: number;
  chargeTimeMin: number;
  latitud?: number;
  longitud?: number;
  Libre: number;
  Total: number;
  groupedCount?: number;
  currentIndex?: number;
}>();

const emit = defineEmits<{
  'navigate-next': [];
  'navigate-previous': [];
  'navigation-error': [error: string];
}>();

const mostrarNavegacionAgrupada = computed(() => props.groupedCount != null && props.groupedCount > 1);
const mostrarBotonesNavegacionAgrupada = computed(() => props.groupedCount != null && props.groupedCount <= 1);

// Calcular el texto y clase de estado según la nueva lógica
const estadoTexto = computed(() => {
  const { Libre, Total } = props;

  // Si Total = 1, el Estado es lo que indique Libre
  if (Total === 1) {
    return Libre === 1 ? t("components.chargerlistitem.libre") : t("components.chargerlistitem.ocupado");
  }

  // Si Total > 1
  if (Total > 1) {
    // Si Libre es igual a Total --> Estado es Libre, mostrar "Total libres"
    if (Libre === Total) {
      return `${Total} ${t("components.chargerlistitem.libres")}`;
    }
    // Si Libre es igual a cero --> Estado es Ocupado, mostrar "Total ocupadas"
    if (Libre === 0) {
      return `${Total} ${t("components.chargerlistitem.ocupadas")}`;
    }
    // Caso por defecto --> Estado es "Libre"(s) de "Total"
    return `${Libre} ${Libre === 1 ? t("components.chargerlistitem.libre_singular") : t("components.chargerlistitem.libres")} ${t("components.chargerlistitem.de")} ${Total}`;
  }

  // Fallback por si Total es 0 o undefined
  return Libre > 0 ? t("components.chargerlistitem.libre") : t("components.chargerlistitem.ocupado");
});

const statusClass = computed(() => {
  const { Libre, Total } = props;

  // Determinar si está libre o ocupado para el estilo
  let isLibre = false;

  if (Total === 1) {
    isLibre = Libre === 1;
  } else if (Total > 1) {
    // Si hay alguna toma libre, se considera parcialmente libre
    isLibre = Libre > 0;
  } else {
    isLibre = Libre > 0;
  }

  return isLibre ? 'status-libre' : 'status-ocupado';
});

function navigateToCharger() {
  if (!props.latitud || !props.longitud) {
    console.warn('No hay coordenadas disponibles para navegar');
    emit('navigation-error', t("components.chargerlistitem.no_coordenadas_disponibles"));
    return;
  }

  const lat = props.latitud;
  const lon = props.longitud;

  // Google Maps URL universal que funciona en todos los dispositivos y navegadores
  // En dispositivos móviles, si hay apps de navegación instaladas (Google Maps, Waze, etc.),
  // el sistema operativo puede ofrecer abrirlas automáticamente
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;

  // Detectar si estamos en un dispositivo móvil
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Timeout para detectar si la navegación falla
  let navigationTimeout: ReturnType<typeof setTimeout> | null = null;
  let errorDetectionTimeout: ReturnType<typeof setTimeout> | null = null;
  let navigationHandled = false;

  // Función para manejar errores de navegación
  const handleNavigationError = (errorMessage: string) => {
    if (!navigationHandled) {
      navigationHandled = true;
      if (navigationTimeout) {
        clearTimeout(navigationTimeout);
      }
      if (errorDetectionTimeout) {
        clearTimeout(errorDetectionTimeout);
        errorDetectionTimeout = null;
      }
      // Limpiar todos los listeners
      window.removeEventListener('error', errorListener, true);
      window.removeEventListener('unhandledrejection', unhandledRejectionListener);
      // Restaurar console.error antes de emitir el error
      if (originalConsoleError) {
        console.error = originalConsoleError;
      }
      emit('navigation-error', errorMessage);
    }
  };

  // Interceptar console.error para capturar errores de esquemas de URL
  const originalConsoleError = console.error;
  let consoleIntercepted = false;
  
  // Función para verificar si un error es relacionado con mapas
  // Solo considerar errores de esquemas personalizados (maps:, geo:), no URLs HTTP/HTTPS
  const isMapNavigationError = (errorString: string): boolean => {
    const lowerError = errorString.toLowerCase();
    // Solo considerar errores de esquemas personalizados, no URLs HTTP/HTTPS
    if ((lowerError.includes('maps:') || lowerError.includes('geo:')) && 
        !lowerError.includes('http://') && !lowerError.includes('https://')) {
      return lowerError.includes('scheme') ||
             lowerError.includes('registered handler') ||
             lowerError.includes('failed to launch') ||
             lowerError.includes('does not have a registered handler');
    }
    return false;
  };
  
  console.error = (...args: any[]) => {
    // Llamar al console.error original primero
    originalConsoleError.apply(console, args);
    
    // Verificar si el error está relacionado con mapas/esquemas
    const errorString = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg?.message) return arg.message;
      if (arg?.toString) return arg.toString();
      return String(arg);
    }).join(' ');
    
    if (isMapNavigationError(errorString)) {
      // Limpiar el timeout de detección de errores si existe
      if (errorDetectionTimeout) {
        clearTimeout(errorDetectionTimeout);
        errorDetectionTimeout = null;
      }
      // Usar nextTick para asegurar que el error se muestre después de que se procese el error
      setTimeout(() => {
        handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
      }, 0);
    }
  };
  consoleIntercepted = true;

  // Listener temporal para capturar errores de eventos relacionados con mapas
  // Solo capturar errores de esquemas personalizados (maps:, geo:), no URLs HTTP/HTTPS
  const errorListener = (event: ErrorEvent) => {
    const errorMessage = event.message || event.error?.message || event.filename || '';
    // Solo capturar errores de esquemas personalizados, no de URLs HTTP/HTTPS
    // En iOS, cuando usamos URLs HTTP/HTTPS, no deberíamos capturar estos errores
    if ((errorMessage.includes('maps:') || errorMessage.includes('geo:')) && 
        !errorMessage.includes('http://') && !errorMessage.includes('https://')) {
      if (errorMessage.includes('scheme') || errorMessage.includes('registered handler') ||
          errorMessage.includes('Failed to launch') ||
          errorMessage.includes('does not have a registered handler')) {
        handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
      }
    }
  };

  // Listener para errores no manejados (promesas rechazadas)
  // Solo capturar errores de esquemas personalizados (maps:, geo:), no URLs HTTP/HTTPS
  const unhandledRejectionListener = (event: PromiseRejectionEvent) => {
    const errorMessage = event.reason?.message || event.reason?.toString() || String(event.reason) || '';
    // Solo capturar errores de esquemas personalizados, no de URLs HTTP/HTTPS
    if ((errorMessage.includes('maps:') || errorMessage.includes('geo:')) && 
        !errorMessage.includes('http://') && !errorMessage.includes('https://')) {
      if (errorMessage.includes('scheme') || errorMessage.includes('registered handler') ||
          errorMessage.includes('Failed to launch') ||
          errorMessage.includes('does not have a registered handler')) {
        handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
      }
    }
  };

  // Agregar listeners temporales ANTES de intentar navegar
  window.addEventListener('error', errorListener, true); // Usar capture phase para capturar más errores
  window.addEventListener('unhandledrejection', unhandledRejectionListener);

  // Timeout para limpiar los listeners después de un tiempo (aumentado a 5 segundos)
  navigationTimeout = setTimeout(() => {
    if (!navigationHandled) {
      // Si después de 5 segundos no se ha manejado el error y no se ha navegado, limpiar listeners
      window.removeEventListener('error', errorListener, true);
      window.removeEventListener('unhandledrejection', unhandledRejectionListener);
      if (consoleIntercepted && originalConsoleError) {
        console.error = originalConsoleError;
      }
    }
  }, 5000);

  // Detectar si estamos en Windows (donde los esquemas maps: y geo: no funcionan)
  // Esta detección debe tener prioridad sobre la detección de móvil
  const isWindows = /Windows|Win32|Win64|WinCE/i.test(navigator.userAgent) || 
                    /Windows Phone/i.test(navigator.userAgent) ||
                    /Win64|WOW64/i.test(navigator.userAgent);
  
  // Si estamos en Windows, siempre usar Google Maps web (no esquemas nativos)
  if (isWindows) {
    // En Windows, usar Google Maps web directamente (los esquemas maps: y geo: no funcionan)
    try {
      const newWindow = window.open(googleMapsUrl, '_blank');
      if (!newWindow) {
        // Si el popup está bloqueado
        handleNavigationError(t("components.chargerlistitem.popup_bloqueado"));
      } else {
        // Si se abrió correctamente, limpiar el listener
        setTimeout(() => {
          window.removeEventListener('error', errorListener, true);
          window.removeEventListener('unhandledrejection', unhandledRejectionListener);
          if (consoleIntercepted && originalConsoleError) {
            console.error = originalConsoleError;
          }
          if (navigationTimeout) {
            clearTimeout(navigationTimeout);
          }
        }, 1000);
      }
    } catch (error: any) {
      window.removeEventListener('error', errorListener, true);
      window.removeEventListener('unhandledrejection', unhandledRejectionListener);
      if (consoleIntercepted && originalConsoleError) {
        console.error = originalConsoleError;
      }
      handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
    }
  } else if (isMobile) {
    // En móviles (excepto Windows), usar esquemas nativos que permiten seleccionar app
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isAndroid) {
      // En Android, el esquema geo: permite al sistema proponer apps disponibles
      // Si no hay apps o falla, el navegador manejará el fallback automáticamente
      try {
        const geoUrl = `geo:${lat},${lon}?q=${lat},${lon}`;
        
        // Detectar cuando la página pierde el foco (indica que se abrió el selector de apps)
        const handleBlur = () => {
          // Si la página pierde el foco, significa que se abrió el selector o una app
          // Cancelar el timeout de error
          if (errorDetectionTimeout) {
            clearTimeout(errorDetectionTimeout);
            errorDetectionTimeout = null;
          }
          navigationHandled = true;
          // Limpiar listeners
          window.removeEventListener('blur', handleBlur);
          window.removeEventListener('visibilitychange', handleVisibilityChange);
          window.removeEventListener('error', errorListener, true);
          window.removeEventListener('unhandledrejection', unhandledRejectionListener);
          if (consoleIntercepted && originalConsoleError) {
            console.error = originalConsoleError;
          }
          if (navigationTimeout) {
            clearTimeout(navigationTimeout);
          }
        };
        
        // Detectar cuando la página se oculta (indica que se abrió una app)
        const handleVisibilityChange = () => {
          if (document.hidden) {
            // La página se ocultó, probablemente se abrió una app
            handleBlur();
          }
        };
        
        // Agregar listeners para detectar cuando se abre el selector/app
        window.addEventListener('blur', handleBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Timeout para detectar si el esquema no funciona (aumentado a 2 segundos)
        // Solo se disparará si no se detecta que se abrió el selector
        errorDetectionTimeout = setTimeout(() => {
          // Solo mostrar error si no se ha manejado la navegación
          if (!navigationHandled) {
            // Limpiar listeners de detección
            window.removeEventListener('blur', handleBlur);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // Si después de 2 segundos no se ha detectado apertura, probablemente falló
            handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
          }
        }, 2000);
        
        // Intentar cambiar la ubicación
        try {
          window.location.href = geoUrl;
        } catch (e) {
          // Si falla inmediatamente, limpiar timeout y listeners, y mostrar error
          window.removeEventListener('blur', handleBlur);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          if (errorDetectionTimeout) {
            clearTimeout(errorDetectionTimeout);
            errorDetectionTimeout = null;
          }
          handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
        }
        
        // Limpiar listeners después de un tiempo si no se detectó apertura
        setTimeout(() => {
          window.removeEventListener('blur', handleBlur);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          if (errorDetectionTimeout) {
            clearTimeout(errorDetectionTimeout);
            errorDetectionTimeout = null;
          }
          window.removeEventListener('error', errorListener, true);
          window.removeEventListener('unhandledrejection', unhandledRejectionListener);
          if (consoleIntercepted && originalConsoleError) {
            console.error = originalConsoleError;
          }
          if (navigationTimeout) {
            clearTimeout(navigationTimeout);
          }
        }, 2000);
      } catch (error: any) {
        if (errorDetectionTimeout) {
          clearTimeout(errorDetectionTimeout);
          errorDetectionTimeout = null;
        }
        window.removeEventListener('error', errorListener, true);
        window.removeEventListener('unhandledrejection', unhandledRejectionListener);
        if (consoleIntercepted && originalConsoleError) {
          console.error = originalConsoleError;
        }
        // Si geo: falla, usar Google Maps web como fallback
        try {
          window.location.href = googleMapsUrl;
        } catch (fallbackError: any) {
          handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
        }
      }
    } else if (isIOS) {
      // En iOS, usar una URL de Google Maps que permita al sistema mostrar selector de apps
      // iOS mostrará un selector si hay Google Maps, Waze u otras apps instaladas
      // Si no hay apps, se abrirá en Safari/Google Maps web
      try {
        // Usar Google Maps URL con parámetros que en iOS mostrará selector de apps
        // El formato con dir= y destination= funciona mejor en iOS para mostrar selector
        const iosMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
        
        // En iOS Safari, usar window.open() primero para que muestre el selector
        // Si el usuario tiene apps instaladas, iOS mostrará un diálogo para elegir
        const newWindow = window.open(iosMapsUrl, '_blank');
        
        if (!newWindow) {
          // Si window.open() falla (popup bloqueado), usar location.href
          // En iOS, esto también puede mostrar el selector dependiendo de la configuración
          window.location.href = iosMapsUrl;
        }
        
        // Marcar como manejado inmediatamente ya que en iOS no hay errores de esquema
        // con URLs HTTP/HTTPS
        navigationHandled = true;
        
        // Limpiar listeners después de un tiempo
        setTimeout(() => {
          if (errorDetectionTimeout) {
            clearTimeout(errorDetectionTimeout);
            errorDetectionTimeout = null;
          }
          window.removeEventListener('error', errorListener, true);
          window.removeEventListener('unhandledrejection', unhandledRejectionListener);
          if (consoleIntercepted && originalConsoleError) {
            console.error = originalConsoleError;
          }
          if (navigationTimeout) {
            clearTimeout(navigationTimeout);
          }
        }, 2000); // Dar tiempo para que iOS muestre el selector si hay apps
      } catch (error: any) {
        // Si hay un error, intentar con location.href como fallback
        try {
          window.location.href = googleMapsUrl;
          navigationHandled = true;
          // Limpiar después de un tiempo
          setTimeout(() => {
            if (errorDetectionTimeout) {
              clearTimeout(errorDetectionTimeout);
              errorDetectionTimeout = null;
            }
            window.removeEventListener('error', errorListener, true);
            window.removeEventListener('unhandledrejection', unhandledRejectionListener);
            if (consoleIntercepted && originalConsoleError) {
              console.error = originalConsoleError;
            }
            if (navigationTimeout) {
              clearTimeout(navigationTimeout);
            }
          }, 2000);
        } catch (fallbackError: any) {
          // Solo mostrar error si ambos métodos fallan
          window.removeEventListener('error', errorListener, true);
          window.removeEventListener('unhandledrejection', unhandledRejectionListener);
          if (consoleIntercepted && originalConsoleError) {
            console.error = originalConsoleError;
          }
          handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
        }
      }
    } else {
      // Otros móviles: usar Google Maps directamente
      try {
        window.location.href = googleMapsUrl;
        setTimeout(() => {
          window.removeEventListener('error', errorListener, true);
          window.removeEventListener('unhandledrejection', unhandledRejectionListener);
          if (consoleIntercepted && originalConsoleError) {
            console.error = originalConsoleError;
          }
          if (navigationTimeout) {
            clearTimeout(navigationTimeout);
          }
        }, 1000);
      } catch (error: any) {
        window.removeEventListener('error', errorListener, true);
        window.removeEventListener('unhandledrejection', unhandledRejectionListener);
        if (consoleIntercepted && originalConsoleError) {
          console.error = originalConsoleError;
        }
        handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
      }
    }
  } else {
    // Desktop: abrir Google Maps en nueva pestaña
    try {
      const newWindow = window.open(googleMapsUrl, '_blank');
      if (!newWindow) {
        // Si el popup está bloqueado
        handleNavigationError(t("components.chargerlistitem.popup_bloqueado"));
      } else {
        // Si se abrió correctamente, limpiar el listener
        setTimeout(() => {
          window.removeEventListener('error', errorListener, true);
          window.removeEventListener('unhandledrejection', unhandledRejectionListener);
          if (consoleIntercepted && originalConsoleError) {
            console.error = originalConsoleError;
          }
          if (navigationTimeout) {
            clearTimeout(navigationTimeout);
          }
        }, 1000);
      }
    } catch (error: any) {
      window.removeEventListener('error', errorListener, true);
      window.removeEventListener('unhandledrejection', unhandledRejectionListener);
      if (consoleIntercepted && originalConsoleError) {
        console.error = originalConsoleError;
      }
      handleNavigationError(t("components.chargerlistitem.error_abrir_mapa"));
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.card {
  border-radius: var(--card-border-radius, 16px);
  border: 1px solid var(--border-default-default, #C2CDDD);
  background: var(--background-default-default, #FFF);
  box-shadow: 0 3px 10px 0 rgba(14, 20, 26, 0.10);
  padding: var(--spacing-md) var(--card-horizontal-padding, 16px) var(--spacing-sm);
}

.title {
  color: var(--text-primary-default, #D3135A);
  text-align: center;
  font-size: var(--font-size-body-lg, 18px);
  font-weight: 700;
  line-height: var(--line-height-body-md, 24px);
  margin-bottom: var(--spacing-md);
}

.meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xs, 8px) var(--spacing-md, 16px);

}

.data {
  display: flex;
  gap: var(--spacing-xs, 8px);

  .icon {
    font-size: var(--font-size-body-md, 18px);
    text-align: center;
    color: var(--text-default-caption, #667790);
    min-width: var(--icon-size-md, 18px);
  }

  .data-content {
    display: flex;
    flex-direction: column;

    .data-title {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
      font-weight: 900;
      line-height: var(--line-height-body-sm, 18px);
    }

    .data-value {
      color: var(--text-default-default, #000);
      font-size: var(--font-size-body-md, 18px);
      line-height: var(--line-height-body-md, 24px);
    }
  }
}


.action {
  display: flex;
  justify-content: flex-end;
  align-items: end;

  .btn {
    border-radius: var(--button-radius, 16px);
    border: 1px solid var(--border-secondary-default, #1863ED);
    background: var(--background-secondary-default, #1863ED);
    text-transform: none;
    color: var(--text-secondary-on-action, #FFF);
    font-size: var(--font-size-body-md, 18px);
    font-weight: 400;
  }
}

.action-full-width {
  grid-column: 1 / -1;
  justify-content: flex-end;
  margin-top: var(--spacing-xs, 8px);
}

.status-libre {
  color: var(--background-success-default, #22C55E);
}

.status-ocupado {
  color: var(--background-error-default, #EF4444);
}

.group-navigation {
  margin-top: var(--spacing-sm, 12px);
  padding-top: var(--spacing-sm, 12px);
  border-top: 1px solid var(--border-default-default, #C2CDDD);
  display: flex;
  justify-content: flex-end;
}

.navigation-controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-md, 16px);
}

.nav-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--border-default-default, #C2CDDD);
  background: var(--background-default-default, #FFF);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-default-default, #000);

  &:hover:not(:disabled) {
    background: var(--background-normal-hover, #F0F0F0);
    border-color: var(--border-secondary-default, #1863ED);
    color: var(--text-secondary-default, #1863ED);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  i {
    font-size: 14px;
  }
}

.counter {
  color: var(--text-default-caption, #667790);
  font-size: var(--font-size-body-sm, 14px);
  font-weight: 600;
  min-width: 60px;
  text-align: center;
}
</style>
